import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ApiError,
  api,
  money,
  planAmountCents,
  type BillingSummary,
  type BillTo,
  type PaymentRow,
  type PlanRow,
} from '../../lib/api';
import {
  brandOf,
  expiryValid,
  formatCardNumber,
  formatExpiry,
  luhnValid,
  preloadAccept,
  tokeniseCard,
} from '../../lib/acceptjs';
import { Drawer } from '@allrouted/ui/Drawer';
import { useUi } from '@allrouted/ui/Toaster';
import { useAuth } from '../AuthGate';

/**
 * Plan, card, vehicles and payments.
 *
 * Read by anybody signed in and changed only by an owner, which is why the
 * plan cards render for everybody and their buttons do not. A dispatcher who
 * has just been refused a vehicle for being over the plan deserves to see the
 * number rather than only the refusal.
 */

const METRICS: { key: string; label: string }[] = [
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'territories', label: 'Territories' },
  { key: 'users', label: 'Users' },
  { key: 'ordersPerMonth', label: 'Orders this month' },
  { key: 'apiKeys', label: 'API keys' },
];

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Free trial',
  trial_expired: 'Trial ended',
  active: 'Active',
  past_due: 'Payment failed',
  canceled: 'Cancelled',
  unpaid: 'Unpaid',
  paused: 'Paused',
};

const TONE: Record<string, string> = {
  active: 'ok',
  approved: 'ok',
  trialing: 'info',
  past_due: 'warn',
  held: 'warn',
  trial_expired: 'late',
  canceled: 'late',
  declined: 'late',
  error: 'late',
  unpaid: 'late',
  refunded: 'idle',
  voided: 'idle',
};

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function BillingView() {
  const ui = useUi();
  const { me, refresh } = useAuth();
  const isOwner = me.user?.role === 'owner';

  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  /* The plan being bought, and how many vehicles, while the drawer is open. */
  const [checkout, setCheckout] = useState<{ plan: PlanRow; vehicles: number } | null>(null);
  const [cardOnly, setCardOnly] = useState(false);
  const [vehicleDraft, setVehicleDraft] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [b, p, pay] = await Promise.all([api.billing(), api.pricingPlans(), api.payments()]);
      setSummary(b);
      setPlans(p.plans);
      setPayments(pay.payments);
      setVehicleDraft(b.vehicles);
      setError(null);
      preloadAccept(b.payment);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load billing.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="empty">{error}</div>;
  if (!summary) return <div className="empty">Loading…</div>;

  const vehiclesInUse = summary.usage.vehicles?.used ?? 0;
  const subscribed = summary.billingScheduled;
  const lapsed = summary.status === 'trial_expired' || summary.status === 'canceled';
  const current = plans.find((p) => p.code === summary.plan.code) ?? null;

  const startCheckout = (plan: PlanRow) => {
    const vehicles = Math.max(plan.min_vehicles, vehiclesInUse, summary.vehicles ?? 0, 1);
    setCardOnly(false);
    setCheckout({ plan, vehicles });
  };

  const applyVehicles = async () => {
    if (vehicleDraft === null || vehicleDraft === summary.vehicles) return;
    setBusy('vehicles');
    try {
      const r = await api.setVehicles(vehicleDraft);
      ui.notify(
        'ok',
        `Billing ${r.vehicles} vehicles`,
        r.proratedCents
          ? `${money(r.proratedCents)} was charged for the rest of this period. From the next charge: ${money(r.amountCents)}/month.`
          : `From the next charge: ${money(r.amountCents)}/month.`,
      );
      await load();
      await refresh();
    } catch (err) {
      ui.notify('error', 'Could not change vehicles', err instanceof ApiError ? err.message : 'Try again.');
    } finally {
      setBusy(null);
    }
  };

  const cancelPlan = async () => {
    const ok = await ui.confirm({
      title: 'Cancel your subscription?',
      body: summary.currentPeriodEnd && summary.status === 'active'
        ? `No further charges will be made. Your team keeps full access until ${fmtDate(summary.currentPeriodEnd)}, and your data stays here if you come back.`
        : 'No charges will be made. Your data stays here if you come back.',
      confirmLabel: 'Cancel subscription',
      danger: true,
    });
    if (!ok) return;
    setBusy('cancel');
    try {
      await api.cancelSubscription(null);
      ui.notify('ok', 'Subscription cancelled', 'You will not be charged again.');
      await load();
      await refresh();
    } catch (err) {
      ui.notify('error', 'Could not cancel', err instanceof ApiError ? err.message : 'Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="billing">
      {/* ---------------- where things stand ---------------- */}
      <section className="billing-hero" data-state={summary.status}>
        <div className="billing-hero-main">
          <span className="eyebrow">Your plan</span>
          <h2>
            {summary.internal ? 'Internal account' : summary.plan.name}
            <span className="pill" data-tone={TONE[summary.status] ?? 'idle'}>
              {STATUS_LABEL[summary.status] ?? summary.status.replace(/_/g, ' ')}
            </span>
          </h2>
          <p className="billing-hero-line">
            {summary.internal ? (
              'Not billed.'
            ) : summary.status === 'trialing' ? (
              <>
                <strong>{summary.trialDaysLeft} {summary.trialDaysLeft === 1 ? 'day' : 'days'}</strong> left
                in your free trial, until {fmtDate(summary.trialEndsAt)}.{' '}
                {subscribed
                  ? `Your first charge of ${money(summary.amountCents)} is on ${fmtDate(summary.trialEndsAt)}.`
                  : 'Choose a plan any time — you will not be charged until the trial ends.'}
              </>
            ) : summary.status === 'trial_expired' ? (
              'Your trial has ended. Everything you set up is still here — choose a plan to keep dispatching.'
            ) : summary.status === 'canceled' ? (
              'This subscription is cancelled. Choose a plan to start again; your data is still here.'
            ) : summary.cancelAtPeriodEnd ? (
              `Cancelled. Access continues until ${fmtDate(summary.currentPeriodEnd)}, with no further charges.`
            ) : (
              <>
                <strong>{money(summary.amountCents)}</strong>/month
                {summary.vehicles ? ` for ${summary.vehicles} vehicles` : ''}. Next charge{' '}
                {fmtDate(summary.currentPeriodEnd)}.
              </>
            )}
          </p>
          {summary.status === 'trialing' && summary.trialDaysLeft !== null ? (
            <TrialMeter daysLeft={summary.trialDaysLeft} endsAt={summary.trialEndsAt} />
          ) : null}
          {summary.lastPaymentError ? (
            <p className="billing-alert" role="alert">
              {summary.lastPaymentError}
            </p>
          ) : null}
        </div>

        {!summary.internal ? (
          <div className="billing-card-on-file">
            <span className="eyebrow">Payment method</span>
            {summary.card ? (
              <div className="card-chip">
                <CardGlyph brand={(summary.card.brand ?? '').toLowerCase()} />
                <span>
                  {summary.card.brand ?? 'Card'} {summary.card.last4 ? `•••• ${summary.card.last4}` : 'on file'}
                </span>
              </div>
            ) : (
              <p className="dim">No card on file.</p>
            )}
            {isOwner && summary.card ? (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setCheckout(null);
                  setCardOnly(true);
                }}
              >
                Update card
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* ---------------- vehicles on a per-vehicle plan ---------------- */}
      {isOwner && subscribed && summary.plan.pricingModel === 'per_vehicle' && current && !lapsed ? (
        <section className="billing-vehicles">
          <div>
            <h3>Vehicles</h3>
            <p className="hint">
              {vehiclesInUse} in use. {money(current.per_vehicle_cents)} per vehicle per month. Adding vehicles
              charges the rest of this period now; removing them applies from the next charge.
            </p>
          </div>
          <div className="billing-vehicles-control">
            <Stepper
              value={vehicleDraft ?? summary.vehicles ?? 1}
              min={Math.max(current.min_vehicles, vehiclesInUse, 1)}
              onChange={setVehicleDraft}
            />
            <span className="billing-vehicles-total">
              {money(planAmountCents(current, vehicleDraft))}/mo
            </span>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy === 'vehicles' || vehicleDraft === summary.vehicles}
              onClick={() => void applyVehicles()}
            >
              {busy === 'vehicles' ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </section>
      ) : null}

      {/* ---------------- plans ---------------- */}
      {!summary.internal ? (
        <section className="billing-plans">
          <h3>{subscribed && !lapsed ? 'Change plan' : 'Choose a plan'}</h3>
          <div className="plan-grid">
            {plans.map((plan) => {
              const isCurrent = plan.code === summary.plan.code && subscribed && !lapsed;
              return (
                <article
                  key={plan.code}
                  className="plan-card"
                  data-current={isCurrent}
                  data-highlight={plan.highlight || undefined}
                >
                  {plan.highlight ? <span className="plan-ribbon">Most popular</span> : null}
                  <header>
                    <h4>{plan.name}</h4>
                    {plan.tagline ? <p className="plan-tagline">{plan.tagline}</p> : null}
                    <p className="plan-price">
                      {plan.pricing_model === 'custom' ? (
                        <span className="plan-poa">Custom pricing</span>
                      ) : plan.pricing_model === 'per_vehicle' ? (
                        <>
                          <strong>{money(plan.per_vehicle_cents, 0)}</strong>
                          <span>/vehicle/month</span>
                        </>
                      ) : (
                        <>
                          <strong>{money(plan.price_cents, 0)}</strong>
                          <span>/month</span>
                        </>
                      )}
                    </p>
                    {plan.pricing_model === 'per_vehicle' && plan.price_cents > 0 ? (
                      <p className="dim">plus {money(plan.price_cents, 0)}/month base</p>
                    ) : null}
                    {plan.pricing_model === 'flat' && plan.limits.vehicles ? (
                      <p className="dim">Up to {plan.limits.vehicles} vehicles</p>
                    ) : null}
                  </header>
                  {plan.description ? <p className="plan-desc">{plan.description}</p> : null}
                  {plan.feature_list.length ? (
                    <ul className="plan-features">
                      {plan.feature_list.map((f) => (
                        <li key={f}>
                          <CheckIcon />
                          {f}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <footer>
                    {isCurrent ? (
                      <span className="plan-badge">Your plan</span>
                    ) : plan.pricing_model === 'custom' ? (
                      <a className="btn" href="mailto:sales@allrouted.com?subject=AllRouted%20Enterprise">
                        Talk to sales
                      </a>
                    ) : isOwner ? (
                      <button
                        className={plan.highlight ? 'btn btn-primary' : 'btn'}
                        type="button"
                        disabled={!summary.payment.enabled}
                        title={summary.payment.enabled ? undefined : 'Card payments are not set up yet'}
                        onClick={() => startCheckout(plan)}
                      >
                        {subscribed && !lapsed ? 'Switch to this plan' : 'Choose'}
                      </button>
                    ) : (
                      <span className="plan-note">An owner can change the plan</span>
                    )}
                  </footer>
                </article>
              );
            })}
          </div>
          {!summary.payment.enabled && isOwner ? (
            <p className="hint">Card payments are being set up. Contact support@allrouted.com to subscribe today.</p>
          ) : null}
        </section>
      ) : null}

      {/* ---------------- usage ---------------- */}
      <section className="billing-usage">
        <h3>Usage</h3>
        <div className="usage-grid">
          {METRICS.map((m) => {
            const u = summary.usage[m.key];
            if (!u) return null;
            return <UsageBar key={m.key} label={m.label} used={u.used} limit={u.limit} />;
          })}
        </div>
      </section>

      {/* ---------------- payments ---------------- */}
      <section className="billing-invoices">
        <h3>Payments</h3>
        {payments.length === 0 ? (
          <p className="empty">No payments yet.</p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Card</th>
                <th>Status</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.occurred_at)}</td>
                  <td>{p.description ?? '—'}</td>
                  <td className="mono">{p.card_last4 ? `•••• ${p.card_last4}` : '—'}</td>
                  <td>
                    <span className="pill" data-tone={TONE[p.status] ?? 'idle'}>
                      {p.status}
                    </span>
                  </td>
                  <td className="num">{money(p.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {isOwner && subscribed && !summary.cancelAtPeriodEnd && !lapsed ? (
        <section className="billing-danger">
          <div>
            <h3>Cancel subscription</h3>
            <p className="hint">Stops future charges. Your team keeps access to the end of the paid period.</p>
          </div>
          <button type="button" className="btn btn-danger" disabled={busy === 'cancel'} onClick={() => void cancelPlan()}>
            {busy === 'cancel' ? 'Cancelling…' : 'Cancel subscription'}
          </button>
        </section>
      ) : null}

      <CheckoutDrawer
        open={!!checkout || cardOnly}
        summary={summary}
        checkout={checkout}
        cardOnly={cardOnly}
        vehiclesInUse={vehiclesInUse}
        onVehicles={(vehicles) => setCheckout((c) => (c ? { ...c, vehicles } : c))}
        onClose={() => {
          setCheckout(null);
          setCardOnly(false);
        }}
        onDone={async (title, body) => {
          setCheckout(null);
          setCardOnly(false);
          ui.notify('ok', title, body);
          await load();
          await refresh();
        }}
        defaultCompany={me.account.name}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Buy a plan, or replace the card.
 *
 * One drawer for both because they are the same form: who is paying and with
 * what. Buying adds the plan summary above it, and lets an owner who already
 * has a card on file switch plans without typing it again.
 */
function CheckoutDrawer({
  open,
  summary,
  checkout,
  cardOnly,
  vehiclesInUse,
  onVehicles,
  onClose,
  onDone,
  defaultCompany,
}: {
  open: boolean;
  summary: BillingSummary;
  checkout: { plan: PlanRow; vehicles: number } | null;
  cardOnly: boolean;
  vehiclesInUse: number;
  onVehicles: (n: number) => void;
  onClose: () => void;
  onDone: (title: string, body: string) => Promise<void>;
  defaultCompany: string;
}) {
  const [useSaved, setUseSaved] = useState(true);
  const [card, setCard] = useState({ number: '', expiry: '', code: '' });
  const [bill, setBill] = useState<BillTo>({ firstName: '', lastName: '', company: defaultCompany, zip: '', address: '', city: '', state: '', country: 'US' });
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setProblem(null);
    setSubmitting(false);
    setUseSaved(!!summary.card && !cardOnly);
    setCard({ number: '', expiry: '', code: '' });
  }, [open, cardOnly, summary.card]);

  const plan = checkout?.plan ?? null;
  const vehicles = checkout?.vehicles ?? 1;
  const amount = plan ? planAmountCents(plan, plan.pricing_model === 'per_vehicle' ? vehicles : null) : 0;
  const inTrial = summary.status === 'trialing' && !!summary.trialEndsAt;
  const switching = summary.billingScheduled && summary.status !== 'canceled' && summary.status !== 'trial_expired';
  const needCard = cardOnly || !summary.card || !useSaved;
  const brand = brandOf(card.number);

  const cardErrors = useMemo(() => {
    if (!needCard) return null;
    if (!bill.firstName.trim() || !bill.lastName.trim()) return 'Enter the cardholder’s name.';
    if (!luhnValid(card.number)) return 'Check the card number.';
    if (!expiryValid(card.expiry)) return 'Check the expiry date (MM/YY).';
    if (card.code.replace(/\D/g, '').length < 3) return 'Enter the security code.';
    if (bill.zip.trim().length < 3) return 'Enter the billing ZIP or postal code.';
    return null;
  }, [needCard, bill, card]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (cardErrors) {
      setProblem(cardErrors);
      return;
    }
    setSubmitting(true);
    setProblem(null);
    try {
      const opaqueData = needCard
        ? await tokeniseCard(summary.payment, {
            number: card.number,
            expiry: card.expiry,
            code: card.code,
            zip: bill.zip,
            fullName: `${bill.firstName} ${bill.lastName}`,
          })
        : undefined;
      const billTo = needCard ? clean(bill) : undefined;

      if (cardOnly) {
        await api.replaceCard({ opaqueData: opaqueData!, billTo: billTo! });
        await onDone('Card updated', 'Future charges will use the new card.');
        return;
      }
      if (!plan) return;
      const r = await api.subscribe({
        planCode: plan.code,
        vehicles: plan.pricing_model === 'per_vehicle' ? vehicles : null,
        opaqueData,
        billTo,
      });
      await onDone(
        switching ? `Moved to ${plan.name}` : `Subscribed to ${plan.name}`,
        r.chargedCents
          ? `${money(r.chargedCents)} was charged. Next charge on ${r.nextChargeOn}.`
          : `${money(r.amountCents)}/month, first charged on ${r.nextChargeOn}.`,
      );
    } catch (err) {
      setProblem(err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong.');
      setSubmitting(false);
    }
  };

  const title = cardOnly ? 'Update card' : switching ? `Switch to ${plan?.name ?? ''}` : `Subscribe to ${plan?.name ?? ''}`;

  return (
    <Drawer
      open={open}
      title={title}
      subtitle={summary.payment.environment === 'sandbox' ? 'Test mode — no real charges' : undefined}
      onClose={onClose}
      footer={
        <div className="drawer-actions">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="checkout-form" className="btn btn-primary" disabled={submitting}>
            {submitting
              ? 'Processing…'
              : cardOnly
                ? 'Save card'
                : switching
                  ? 'Confirm change'
                  : inTrial
                    ? 'Start subscription'
                    : `Pay ${money(amount)}`}
          </button>
        </div>
      }
    >
      <form id="checkout-form" className="checkout" onSubmit={(e) => void submit(e)}>
        {!cardOnly && plan ? (
          <div className="checkout-summary">
            <div className="checkout-line">
              <span>{plan.name}</span>
              <strong>{money(amount)}/month</strong>
            </div>
            {plan.pricing_model === 'per_vehicle' ? (
              <div className="checkout-vehicles">
                <span>
                  Vehicles <small>{money(plan.per_vehicle_cents)} each</small>
                </span>
                <Stepper
                  value={vehicles}
                  min={Math.max(plan.min_vehicles, vehiclesInUse, 1)}
                  onChange={onVehicles}
                />
              </div>
            ) : null}
            <p className="checkout-note">
              {switching
                ? 'The new price applies from your next charge. Nothing is charged today.'
                : inTrial
                  ? `Nothing is charged today. Your first charge is on ${fmtDate(summary.trialEndsAt)}, when the trial ends, then monthly.`
                  : 'Charged today, then monthly on this date. Cancel any time.'}
            </p>
          </div>
        ) : null}

        {!cardOnly && summary.card ? (
          <div className="checkout-choice" role="radiogroup" aria-label="Payment method">
            <button
              type="button"
              role="radio"
              aria-checked={useSaved}
              className="choice"
              onClick={() => setUseSaved(true)}
            >
              <span className="choice-dot" />
              {summary.card.brand ?? 'Card'} {summary.card.last4 ? `•••• ${summary.card.last4}` : 'on file'}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!useSaved}
              className="choice"
              onClick={() => setUseSaved(false)}
            >
              <span className="choice-dot" />
              Use a different card
            </button>
          </div>
        ) : null}

        {needCard ? (
          <div className="card-form">
            <div className="card-form-row">
              <LabeledInput label="First name" value={bill.firstName} autoComplete="cc-given-name" onChange={(v) => setBill((b) => ({ ...b, firstName: v }))} />
              <LabeledInput label="Last name" value={bill.lastName} autoComplete="cc-family-name" onChange={(v) => setBill((b) => ({ ...b, lastName: v }))} />
            </div>
            <label className="card-input">
              <span>Card number</span>
              <div className="card-number">
                <input
                  inputMode="numeric"
                  autoComplete="cc-number"
                  placeholder="1234 5678 9012 3456"
                  value={card.number}
                  onChange={(e) => setCard((c) => ({ ...c, number: formatCardNumber(e.target.value) }))}
                />
                <CardGlyph brand={brand} />
              </div>
            </label>
            <div className="card-form-row">
              <LabeledInput
                label="Expiry"
                value={card.expiry}
                placeholder="MM/YY"
                inputMode="numeric"
                autoComplete="cc-exp"
                onChange={(v) => setCard((c) => ({ ...c, expiry: formatExpiry(v) }))}
              />
              <LabeledInput
                label="Security code"
                value={card.code}
                placeholder={brand === 'amex' ? '4 digits' : '3 digits'}
                inputMode="numeric"
                autoComplete="cc-csc"
                onChange={(v) => setCard((c) => ({ ...c, code: v.replace(/\D/g, '').slice(0, 4) }))}
              />
            </div>
            <LabeledInput label="Company (optional)" value={bill.company ?? ''} autoComplete="organization" onChange={(v) => setBill((b) => ({ ...b, company: v }))} />
            <LabeledInput label="Billing address (optional)" value={bill.address ?? ''} autoComplete="address-line1" onChange={(v) => setBill((b) => ({ ...b, address: v }))} />
            <div className="card-form-row card-form-row-3">
              <LabeledInput label="City" value={bill.city ?? ''} autoComplete="address-level2" onChange={(v) => setBill((b) => ({ ...b, city: v }))} />
              <LabeledInput label="State" value={bill.state ?? ''} autoComplete="address-level1" onChange={(v) => setBill((b) => ({ ...b, state: v }))} />
              <LabeledInput label="ZIP" value={bill.zip} autoComplete="postal-code" onChange={(v) => setBill((b) => ({ ...b, zip: v }))} />
            </div>
            <p className="checkout-secure">
              <LockIcon /> Card details go directly to Authorize.net and are never stored by AllRouted.
            </p>
          </div>
        ) : null}

        {problem ? (
          <p className="billing-alert" role="alert">
            {problem}
          </p>
        ) : null}
      </form>
    </Drawer>
  );
}

function clean(b: BillTo): BillTo {
  const out: BillTo = { firstName: b.firstName.trim(), lastName: b.lastName.trim(), zip: b.zip.trim() };
  for (const k of ['company', 'address', 'city', 'state', 'country'] as const) {
    const v = b[k]?.trim();
    if (v) out[k] = v;
  }
  return out;
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'numeric' | 'text';
}) {
  return (
    <label className="card-input">
      <span>{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** A count with buttons either side. Typed values are clamped, not refused. */
export function Stepper({
  value,
  min,
  max = 10_000,
  onChange,
}: {
  value: number;
  min: number;
  max?: number;
  onChange: (n: number) => void;
}) {
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n)));
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <div className="stepper">
      <button type="button" aria-label="Fewer" disabled={value <= min} onClick={() => onChange(clamp(value - 1))}>
        −
      </button>
      <input
        inputMode="numeric"
        aria-label="Vehicles"
        value={text}
        onChange={(e) => setText(e.target.value.replace(/\D/g, ''))}
        onBlur={() => {
          const n = clamp(Number(text) || min);
          setText(String(n));
          onChange(n);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'ArrowUp') onChange(clamp(value + 1));
          if (e.key === 'ArrowDown') onChange(clamp(value - 1));
        }}
      />
      <button type="button" aria-label="More" disabled={value >= max} onClick={() => onChange(clamp(value + 1))}>
        +
      </button>
    </div>
  );
}

function TrialMeter({ daysLeft, endsAt }: { daysLeft: number; endsAt: string | null }) {
  /* Shown against two weeks; a longer trial simply starts full. */
  const ratio = Math.max(0, Math.min(1, daysLeft / 14));
  return (
    <div className="trial-meter" data-low={daysLeft <= 3 || undefined} title={endsAt ? `Ends ${fmtDate(endsAt)}` : undefined}>
      <i style={{ width: `${Math.max(3, ratio * 100)}%` }} />
    </div>
  );
}

/**
 * Usage against a limit.
 *
 * Amber from four fifths and red at the ceiling, using the same colours the
 * board uses for a route running late -- on this screen as on that one, colour
 * means something is about to stop working.
 */
function UsageBar({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const ratio = limit === null || limit === 0 ? 0 : Math.min(1, used / limit);
  const state = limit === null ? 'none' : ratio >= 1 ? 'full' : ratio >= 0.8 ? 'near' : 'ok';

  return (
    <div className="usage" data-state={state}>
      <div className="usage-head">
        <span>{label}</span>
        <strong>
          {used.toLocaleString()}
          {limit === null ? '' : ` / ${limit.toLocaleString()}`}
        </strong>
      </div>
      <div className="usage-track">
        <i style={{ width: limit === null ? '100%' : `${Math.max(2, ratio * 100)}%` }} />
      </div>
      {limit === null ? <em>Unlimited</em> : null}
    </div>
  );
}

function CardGlyph({ brand }: { brand: string }) {
  const label =
    brand === 'visa' ? 'VISA' : brand === 'mastercard' ? 'MC' : brand === 'amex' ? 'AMEX' : brand === 'discover' ? 'DISC' : '';
  return (
    <span className="card-glyph" data-brand={brand || 'unknown'} aria-hidden="true">
      {label || (
        <svg viewBox="0 0 24 16" width="22" height="15">
          <rect x="0.5" y="0.5" width="23" height="15" rx="2.5" fill="none" stroke="currentColor" />
          <rect x="0.5" y="4" width="23" height="3" fill="currentColor" />
        </svg>
      )}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" fill="currentColor" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
